/** Shared mic level sampling for the setup sound check (display + silence detection). */

export const ANALYSER_FFT_SIZE = 2048;

export type MicSample = {
  /** Linear peak amplitude 0–1 (conservative — used for silence detection). */
  peak: number;
  /** Linear RMS amplitude 0–1. */
  rms: number;
  /** Log-compressed 0–100 value for the level meter. */
  display: number;
};

export const resumeAudioContext = async (ctx: AudioContext): Promise<void> => {
  if (ctx.state === 'suspended') await ctx.resume();
};

export const configureAnalyser = (analyser: AnalyserNode): void => {
  analyser.fftSize = ANALYSER_FFT_SIZE;
  analyser.smoothingTimeConstant = 0.75;
};

/** Linear amplitude below this reads as silence on the meter (Web Audio path). */
export const ANALYSER_DISPLAY_FLOOR = 0.008;

/** Agora idle noise below this reads as silence (matches device mic test 1% threshold). */
export const SDK_VOLUME_NOISE_FLOOR = 0.01;

/** Map linear amplitude to a 0–100 meter reading; quiet mobile browsers still move the bar. */
export const amplitudeToDisplayPercent = (amplitude: number): number => {
  if (amplitude <= ANALYSER_DISPLAY_FLOOR) return 0;
  const db = 20 * Math.log10(Math.max(amplitude, 1e-6));
  // Treat ~-55 dBFS as silence, ~-12 dBFS as full bar.
  const normalized = (db + 55) / 43;
  return Math.round(Math.min(100, Math.max(0, normalized * 100)));
};

// Meter smoothing/normalisation tuned for a ~50ms sample tick (see the two callers).
const METER_ATTACK = 0.5; // fast rise so the bar reacts to speech immediately
const METER_RELEASE = 0.12; // slow fall so it glides down instead of flickering
const ENVELOPE_DECAY = 0.97; // recent-peak decay (~1s half-life at 50ms) for normalisation
const MIN_ENVELOPE = 0.04; // floor so quiet idle noise can't normalise up to a full bar

/**
 * A stateful level meter: normalises the reading to the recent loud peak — so the bar fills similarly
 * whether the browser runs hot (Chrome) or quiet (Firefox/Safari/Android) — and smooths it with a fast
 * attack / slow release so it glides instead of jumping like the raw log curve did. Returns 0–100.
 * Drive it from a fixed ~50ms tick; keep using the raw `peak` for silence/speech detection.
 */
export const createLevelMeter = () => {
  let envelope = 0;
  let display = 0;
  return ({ rms, peak }: Pick<MicSample, 'rms' | 'peak'>): number => {
    const amplitude = Math.max(rms, peak * 0.35);
    let target = 0;
    if (amplitude > ANALYSER_DISPLAY_FLOOR) {
      envelope = Math.max(amplitude, envelope * ENVELOPE_DECAY);
      target = Math.min(100, (amplitude / Math.max(envelope, MIN_ENVELOPE)) * 100);
    }
    const coeff = target > display ? METER_ATTACK : METER_RELEASE;
    display += (target - display) * coeff;
    return Math.round(Math.max(0, Math.min(100, display)));
  };
};

export const sampleAnalyser = (analyser: AnalyserNode, buffer: Float32Array): MicSample => {
  analyser.getFloatTimeDomainData(buffer);
  let sumSquares = 0;
  let peak = 0;
  for (let i = 0; i < buffer.length; i += 1) {
    const sample = buffer[i];
    const abs = Math.abs(sample);
    if (abs > peak) peak = abs;
    sumSquares += sample * sample;
  }
  const rms = Math.sqrt(sumSquares / buffer.length);
  const display = amplitudeToDisplayPercent(Math.max(rms, peak * 0.35));
  return { peak, rms, display };
};

/**
 * Agora `getVolumeLevel()` is already normalised 0–1 — use linear scaling like the device
 * mic test. Do not apply the Web Audio log curve here; it pegs the bar on Chrome idle noise.
 */
export const sdkVolumeToDisplayPercent = (volume: number): number => {
  if (!Number.isFinite(volume) || volume <= SDK_VOLUME_NOISE_FLOOR) return 0;
  return Math.round(Math.min(100, volume * 100));
};
